import React, { useState } from 'react'
import { message, Table, TableColumnsType } from 'antd'
import { Box, Button, Grid, IconButton, Typography } from '@mui/material'
import dayjs from 'dayjs'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import { IFilterMapped } from './LeadListFollowFilter'
import {
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../../../constants/global'
import { axiosInstance } from '../../../../../../../hooks/useAxios'
import CircularLoading from '../../../../../../../components/CircularProgress'
import { Tag } from '../../../../../../../components/Tag'
import { Link, useNavigate } from 'react-router-dom'
import PageUrls from '../../../../../../../constants/page-urls'
import Pagination from '../../../../../../../components/Pagination'
import AddIcCallIcon from '@mui/icons-material/AddIcCall'
import {
  ETrackingNotificationsStatusCalled,
  ETrackingNotificationsStatusFollow,
  ITrackingNotifications,
} from '../../../../../../../types/trackingNotification'
import { ILead } from '../../../../../../../types/lead'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CallIcon from '@mui/icons-material/Call'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../../constants/errors'

interface ILeadListFollowTableProps {
  selectedData: ILead[]
  setSelectedData: (leads: ILead[]) => void
  isLoading: boolean
  data?: ITrackingNotifications[]
  onUpdateTrackingNotificationListSuccess: () => void
  filter: IFilterMapped
  onSearch: (filter: IFilterMapped) => void
  handlePageChange: (filter: IFilterMapped) => void
  total: number
}

const LeadListFollowTable = ({
  selectedData,
  setSelectedData,
  isLoading,
  data,
  onUpdateTrackingNotificationListSuccess,
  filter,
  onSearch,
  handlePageChange,
  total,
}: ILeadListFollowTableProps) => {
  const navigate = useNavigate()
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false)

  const columns: TableColumnsType<ITrackingNotifications & { key: string }> = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'วันที่แจ้งเตือน',
      dataIndex: 'expiredAt',
      key: 'expiredAt',
      width: 160,
      align: 'center' as const,
      render: (expiredAt: string) => {
        return (
          <Box>
            <Tag
              color={dayjs().isAfter(dayjs(expiredAt)) ? 'error' : 'success'}
            >
              <Typography variant="subtitle2">
                {dayjs().isAfter(dayjs(expiredAt)) ? 'หมดอายุ' : 'ใช้งานได้'}
              </Typography>
            </Tag>
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(expiredAt).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(expiredAt).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'รายการ LEAD',
      dataIndex: 'lead',
      key: 'lead',
      align: 'center' as const,
      render: (lead: ILead) => {
        return (
          <Box sx={{ minWidth: 200, textAlign: 'left' }}>
            <Typography variant="subtitle1" color="text.secondary">
              {lead?.name}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1 }}
              color="text.secondary"
            >
              ผู้ดูแล Lead: {lead?.assignTo?.name}
            </Typography>

            <Typography variant="subtitle2" color="text.secondary">
              ลูกค้า: {lead.customer?.name ?? '-'}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'PIPLINE',
      dataIndex: 'lead',
      key: 'lead',
      width: 140,
      align: 'center' as const,
      render: (lead: ILead) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {lead.pipelineName}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'สถานะ',
      dataIndex: 'lead',
      key: 'lead',
      width: 120,
      align: 'center' as const,
      render: (lead: ILead) => {
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Tag color={'info'}>
              <Typography variant="subtitle2">
                {lead.activePipelineItemName}
              </Typography>
            </Tag>
          </Box>
        )
      },
    },
    {
      title: 'ผู้ติดตาม',
      dataIndex: 'assignTo',
      key: 'assignTo',
      width: 140,
      align: 'center' as const,
      render: (assignTo: any) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {assignTo?.username}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'note',
      key: 'note',
      align: 'center' as const,
      render: (note: string) => {
        return (
          <Box sx={{ minWidth: 130, textAlign: 'center' }}>
            {note ? note : <em>-</em>}
          </Box>
        )
      },
    },
    {
      title: 'สถานะการติดตาม',
      dataIndex: 'statusFollow',
      key: 'statusFollow',
      width: 150,
      align: 'center' as const,
      render: (
        statusFollow: ETrackingNotificationsStatusFollow,
        trackingNotification: ITrackingNotifications,
      ) => {
        return (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                statusFollow === ETrackingNotificationsStatusFollow.WAIT ? (
                  <AccessTimeIcon color={'warning'} />
                ) : (
                  <CheckCircleOutlineIcon color={'info'} />
                )
              }
              color={
                statusFollow === ETrackingNotificationsStatusFollow.WAIT
                  ? 'warning'
                  : 'info'
              }
              onClick={() =>
                changeStatusFollow(
                  statusFollow === ETrackingNotificationsStatusFollow.DONE
                    ? ETrackingNotificationsStatusFollow.WAIT
                    : ETrackingNotificationsStatusFollow.DONE,
                  trackingNotification?.objectId!,
                )
              }
            >
              <Typography variant="subtitle2">
                {statusFollow === ETrackingNotificationsStatusFollow.WAIT
                  ? 'ยังไม่ติดตาม'
                  : 'ติดตามแล้ว'}
              </Typography>
            </Button>
          </>
        )
      },
    },
    {
      title: 'การโทร',
      dataIndex: 'statusCalled',
      key: 'statusCalled',
      width: 160,
      align: 'center' as const,
      render: (
        statusCalled: ETrackingNotificationsStatusCalled,
        trackingNotification: ITrackingNotifications,
      ) => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            {statusCalled === ETrackingNotificationsStatusCalled.NOT_CALLED ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    <CallIcon
                      sx={{ transform: 'rotate(-45deg)' }}
                      color={'success'}
                    />
                  }
                  color={'success'}
                  onClick={() =>
                    changeStatusCall(
                      ETrackingNotificationsStatusCalled.CALLED,
                      trackingNotification?.objectId!,
                    )
                  }
                >
                  โทรแล้ว
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    <AddIcCallIcon
                      sx={{ transform: 'rotate(-45deg)' }}
                      color={'error'}
                    />
                  }
                  color={'error'}
                  onClick={() =>
                    changeStatusCall(
                      ETrackingNotificationsStatusCalled.CANCEL_CALLED,
                      trackingNotification?.objectId!,
                    )
                  }
                >
                  ไม่รับสาย
                </Button>
              </>
            ) : (
              <>
                <Tag
                  color={
                    statusCalled ===
                    ETrackingNotificationsStatusCalled.CANCEL_CALLED
                      ? 'error'
                      : 'success'
                  }
                >
                  <Typography variant="subtitle2">
                    {statusCalled ===
                    ETrackingNotificationsStatusCalled.CANCEL_CALLED
                      ? 'ไม่รับสาย'
                      : 'โทรแล้ว'}
                  </Typography>
                </Tag>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<AccessTimeIcon color={'info'} />}
                  color={'info'}
                  onClick={() =>
                    changeStatusCall(
                      ETrackingNotificationsStatusCalled.NOT_CALLED,
                      trackingNotification?.objectId!,
                    )
                  }
                >
                  ยกเลิก
                </Button>
              </>
            )}
          </Box>
        )
      },
    },
    {
      dataIndex: '',
      key: 'action',
      width: 80,
      align: 'center' as const,
      render: (_: undefined, trackingNotification: ITrackingNotifications) => {
        return (
          <Box>
            <IconButton
              component={Link}
              to={`${PageUrls.CRM.Lead.Detail}/${trackingNotification.lead.objectId}`}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        )
      },
    },
  ]

  const changeStatusFollow = async (
    status: ETrackingNotificationsStatusFollow,
    trackingNotificationObjectId: string,
  ) => {
    try {
      await axiosInstance.put(
        `/lead/change-status-tracking-notification-follow/${trackingNotificationObjectId}`,
        {
          status,
        },
      )
      message.success('แก้ไขสถานะสำเร็จ')
      onUpdateTrackingNotificationListSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const changeStatusCall = async (
    status: ETrackingNotificationsStatusCalled,
    trackingNotificationObjectId: string,
  ) => {
    try {
      await axiosInstance.put(
        `/lead/change-status-tracking-notification-called/${trackingNotificationObjectId}`,
        {
          status,
        },
      )
      message.success('แก้ไขสถานะสำเร็จ')
      onUpdateTrackingNotificationListSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const deleteTrackingNotification = async (
    trackingNotificationObjectId: string,
  ) => {
    try {
      await axiosInstance.delete(
        `/lead/remove-tracking-notification/${trackingNotificationObjectId}`,
      )
      message.success('ลบสำเร็จ')
      onUpdateTrackingNotificationListSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    const keys = newSelectedRowKeys as string[]
    const leadObjects = data?.reduce<{ [key: string]: ILead }>((acc, cur) => {
      acc[cur?.lead.objectId as string] = cur.lead
      return acc as any
    }, {})
    const leads = keys.map((key: string) => {
      return leadObjects?.[key]
    })
    setSelectedData(leads as ILead[])
  }

  const rowSelection = {
    selectedRowKeys: selectedData.map(
      (lead: ILead) =>
        data?.find(
          (trackingNotification: ITrackingNotifications) =>
            trackingNotification.lead.objectId === lead.objectId,
        )?.lead.objectId as string,
    ),
    onChange: onSelectChange,
  }

  return (
    <>
      <CircularLoading isLoading={isLoading || isLoadingDelete} />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
        mb={2}
      />
      <Table
        size={'middle'}
        rowSelection={rowSelection}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map(
          (trackingNotification: ITrackingNotifications, index: number) => {
            return {
              ...trackingNotification,
              key: trackingNotification.lead.objectId!,
              numberRow:
                index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
            }
          },
        )}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default LeadListFollowTable
